import React from 'react';
import { Button, Grid } from '@mui/material';
import { Add } from '@mui/icons-material';
import { Category as CategoryComponent } from '@Components/Category/Category.component';
import { setCoFConfig } from '@Actions';
import { useDispatch } from 'react-redux';
import { Category } from '@Types/category.types';
import { Component, ComponentProps } from '@Types/component.types';
import { useGlobalization } from '@Translations/useGlobalization';

interface RenderedCoFCategoriesProps extends ComponentProps {
	categories: Category[];
	totalWeight: number;
	handleAddCategoryClicked: () => void;
}

// Only ever rendered by CoFSetup while useIsRiskTemplatesEnabled() is on,
// and only once loading has finished (CoFSetup shows SetupCategoriesSkeleton
// itself while loading) — so there's no loading state to handle here. The
// classic (flag-off) layout lives in its own ClassicCoFCategories
// component, unchanged, so it's easy to diff against main (same approach
// as Category.component.tsx's Classic* split).
export const RenderedCoFCategories: React.FC<RenderedCoFCategoriesProps> = ({
	categories,
	totalWeight,
	onAddComponent,
	onEditComponent,
	handleAddCategoryClicked,
}) => {
	const dispatch = useDispatch();
	const { t } = useGlobalization();

	const onCategoryWeightingChanged = (index: number) => (value: number) => {
		dispatch(
			setCoFConfig(
				categories.map((onChangeCat, onChangeIndex) =>
					onChangeIndex === index
						? {
								...onChangeCat,
								weighting: value,
						  }
						: onChangeCat,
				),
			),
		);
	};

	// CoF has no "inspection" concept (unlike LoF's Condition category), so
	// forInspection is always false regardless of the category. The forLoF
	// parameter is part of Category's onAddComponent/onEditComponent
	// signature but unused here — prefixed with _ to signal that.
	const addComponentToCoF = (
		_forLoF: boolean,
		targetCat: Category | undefined,
	): void => {
		onAddComponent(false, false, targetCat);
	};

	const editComponentForCoF = (
		_forLoF: boolean,
		targetCat: Category | undefined,
		component: Component,
	): void => {
		onEditComponent(false, false, targetCat, component);
	};

	return (
		<>
			{categories.map((cat, index) => (
				<Grid
					item
					xs={12}
					key={`${cat.name}-${index}`}
					style={{ paddingBottom: '12px' }}>
					<CategoryComponent
						isLoading={false}
						forLoF={false}
						LoFCategoryIndex={index}
						isWeightingInvalid={totalWeight != 100}
						onCategoryWeightingChanged={onCategoryWeightingChanged(
							index,
						)}
						onAddComponent={addComponentToCoF}
						onEditComponent={editComponentForCoF}
					/>
				</Grid>
			))}
			<Grid
				item
				xs={12}
				container
				justifyContent="flex-start"
				sx={{ pb: 5 }}>
				<Button
					style={{ marginTop: '4px' }}
					variant="outlined"
					color="primary"
					startIcon={<Add />}
					onClick={handleAddCategoryClicked}>
					{t('Add Category')}
				</Button>
			</Grid>
		</>
	);
};
